/**
 * Construye la evidencia exportable de una ejecución. Nunca incluye el payload original
 * ni secretos (08-seguridad-privacidad.md): solo resumen, hallazgos y supuestos.
 */
import type { EngineOutput } from "./types";

export interface ExportReport {
  runId: string;
  schemaVersion: string;
  status: string;
  summary: string;
  findings: EngineOutput["findings"];
  evidence: EngineOutput["evidence"];
  assumptions: string[];
  generatedAt: string;
}

const DEFAULT_ASSUMPTIONS = [
  "Ejecución en modo determinista; ningún dato salió del navegador.",
  "Los datos originales de entrada se excluyen de esta exportación por diseño.",
];

export function buildExportReport(
  output: EngineOutput,
  clock: () => Date = () => new Date(),
): ExportReport {
  return {
    runId: output.runId,
    schemaVersion: output.schemaVersion,
    status: output.status,
    summary: output.summary,
    findings: output.findings,
    evidence: output.evidence,
    assumptions: DEFAULT_ASSUMPTIONS,
    generatedAt: clock().toISOString(),
  };
}

export function exportReportToJson(report: ExportReport): string {
  return JSON.stringify(report, null, 2);
}

export function exportReportToMarkdown(report: ExportReport): string {
  const lines = [
    `# Evidencia de ejecución ${report.runId}`,
    "",
    `- Estado: ${report.status}`,
    `- Versión de contrato: ${report.schemaVersion}`,
    `- Versión de reglas: ${report.evidence.rulesVersion}`,
    `- Escenario: ${report.evidence.scenarioId}`,
    `- Generado: ${report.generatedAt}`,
    "",
    `## Resumen`,
    "",
    report.summary,
    "",
    `## Hallazgos (${report.findings.length})`,
    "",
  ];
  if (report.findings.length === 0) {
    lines.push("Sin hallazgos.");
  } else {
    for (const finding of report.findings) {
      lines.push(`- **[${finding.severity}] ${finding.ruleId}** — ${finding.message}`);
      if (finding.suggestion) lines.push(`  - Sugerencia: ${finding.suggestion}`);
    }
  }
  lines.push("", "## Supuestos", "");
  for (const assumption of report.assumptions) lines.push(`- ${assumption}`);
  lines.push("");
  return lines.join("\n");
}
